/* eslint-disable camelcase */
import Cookies from 'js-cookie'
import { App, computed } from 'vue'
import { createI18n } from 'vue-i18n'
import enLocal from './en'
import zhLocal from './zh'
import frLocal from './fr'
import itLocal from './it'
import nlLocal from './nl'
import router from '@/router/index'
import store from '@/store/index'
import { MicroAppCacheKey } from '@/enums/microAppCacheKey'

export const langKey = 'lang'

export function isI18nDevMode(): boolean {
  return localStorage.getItem(MicroAppCacheKey.DevelopModeKey) === 'true'
}

export function setI18nDevMode(enabled: boolean) {
  if (enabled) {
    localStorage.setItem(MicroAppCacheKey.DevelopModeKey, true as any)
  } else {
    localStorage.removeItem(MicroAppCacheKey.DevelopModeKey)
  }
}

export const langKeyMap = {
  zh: 'zh',
  en: 'en',
  fr: 'fr',
  nl: 'nl',
  it: 'it'
}

export const langTextMap = {
  [langKeyMap.zh]: '简体中文',
  [langKeyMap.en]: 'English',
  [langKeyMap.fr]: 'Français',
  [langKeyMap.nl]: 'Nederlands',
  [langKeyMap.it]: 'Italia'
}

type LangMapKey = keyof typeof langTextMap

type Message = Record<LangMapKey, {[key: string]: any}>

const messages = {
  [langKeyMap.en]: enLocal,
  [langKeyMap.zh]: zhLocal,
  [langKeyMap.fr]: frLocal,
  [langKeyMap.it]: itLocal,
  [langKeyMap.nl]: nlLocal
}

export const i18n: any = createI18n({
  locale: getLanguage(), // set locale
  fallbackLocale: langKeyMap.zh, // set fallback locale
  messages, // set locale messages
  silentTranslationWarn: true,
  legacy: false,
  missingWarn: false,
  fallbackWarn: false
})

export function getLanguage() {
  const chooseLanguage = localStorage.getItem(langKey) || Cookies.get(langKey)
  const validLocales = Object.keys(messages)
  if (chooseLanguage && validLocales.includes(chooseLanguage)) return chooseLanguage
  if (chooseLanguage && !validLocales.includes(chooseLanguage)) {
    Cookies.remove(langKey)
    localStorage.removeItem(langKey)
  }

  // if has not choose language
  const language = (navigator.language || navigator.browserLanguage || '')
    .substring(0, 2)
    .toLowerCase()
  const locales = Object.keys(messages)
  for (const locale of locales) {
    if (new RegExp(locale).test(language)) {
      return locale
    }
  }
  return langKeyMap.zh
}

export interface SetLanguageOptions {
  /** 仅持久化不更新 locale，用于切换后 reload 前避免词条闪烁 */
  persistOnly?: boolean
}

export function setLanguage(val: LangMapKey, options?: SetLanguageOptions) {
  if (!options?.persistOnly) {
    i18n.global.locale.value = val
  }
  Cookies.set(langKey, val as string)
  localStorage.setItem(langKey, val as string)
  window.postMessage(
    {
      type: 'changeLanguage',
      data: {
        lang: val
      }
    },
    window.origin
  )
}

export const $t = (key: string, ...args: any[]) => {
  if (key == null || key === '') return ''
  // 开发者模式：展示词条 key；词条模式：展示翻译
  if (isI18nDevMode()) return key
  return i18n.global.t(key, ...args) || key
}

export function installI18n(app: App<Element>) {
  app.use(i18n)
  app.provide('$t', $t)
  app.config.globalProperties.$t = $t
}

/**
 * 设置页面标题，逻辑根据router/index 内同步
 */
export function setDocumentTitle() {
  const to: any = router.currentRoute.value
  if (to.meta?.title) {
    document.title = to.meta.title
  }
  if (to.path === '/login') {
    document.title = $t('main_login_btn_text')
  } else {
    if (to.path === '/') {
      document.title = $t('main_home_home_name')
    } else {
      const state: any = store.state
      const pageList: [] = state.menu.pageList
      const title: string = to.query?.title || ''
      const matchedRoute: any = pageList.find((page: any) => {
        return to.path === page.routeUrl
      })
      if (matchedRoute) {
        const key = title || matchedRoute.localKey
        document.title = key ? $t(key) : matchedRoute.name
      } else {
        document.title = '404'
      }
    }
  }
}

/**
 * 是否为中文
 */
export const isCN = computed(() => {
  return i18n.global.locale.value === langKeyMap.zh
})
